import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import useFetchData from '../../../../hooks/UseFetchData'
import Table from '../../../../components/his/invoices/Table'
import PageHeader from '../../../../shared/PageHeader'
import CustomButton from '../../../../shared/CustomButton'
import ReportFilters from '../../../../shared/ReportFilters'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf,
  FaSearch,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import moment from 'moment'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { AmiriRegBase64 } from '../../../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../../../assets/fonts/AmiriB'

const summaryColumns = t => [
  {
    header: t('Department'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.Department || t('Unassigned'),
  },
  {
    header: t('Total Patients'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.Count ?? 0,
  },
  {
    header: t('Unique Patients'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.PatientCount ?? 0,
  },
]

const detailColumns = t => [
  ...summaryColumns(t),
  {
    header: t('Patient Name'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.PatientName || '-',
  },
  {
    header: t('File No'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.FileNo || '-',
  },
  {
    header: t('Document ID'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.DocumentID || '-',
  },
  {
    header: t('Practitioner Name'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.PractitionerName || '-',
  },
  {
    header: t('Visit ID'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.VisitID ?? '-',
  },
  {
    header: t('Visit Date'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.VisitDate || '-',
  },
  {
    header: t('Visit'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.Visit || row?.VisitCategory || '-',
  },
]

const flattenDetails = groups =>
  (Array.isArray(groups) ? groups : []).flatMap(group => {
    const details = Array.isArray(group?.Details) ? group.Details : []
    return details.map(detail => ({
      Department: group?.Department,
      PatientCount: group?.PatientCount,
      Count: group?.Count,
      ...detail,
    }))
  })

const exportToExcel = (data, details, t, payloadData) => {
  const rows = details ? flattenDetails(data) : data
  const formatted = (Array.isArray(rows) ? rows : []).map(row => {
    const base = {
      [t('Department')]: row?.Department || t('Unassigned'),
      [t('Total Patients')]: row?.Count ?? 0,
      [t('Unique Patients')]: row?.PatientCount ?? 0,
    }
    if (!details) return base

    return {
      ...base,
      [t('Patient Name')]: row?.PatientName || '',
      [t('File No')]: row?.FileNo || '',
      [t('Document ID')]: row?.DocumentID || '',
      [t('Practitioner Name')]: row?.PractitionerName || '',
      [t('Visit ID')]: row?.VisitID ?? '',
      [t('Visit Date')]: row?.VisitDate || '',
      [t('Visit')]: row?.Visit || row?.VisitCategory || '',
    }
  })

  const headers = Object.keys(formatted?.[0] || {})
  const worksheet = XLSX.utils.aoa_to_sheet([
    [payloadData.business_en || payloadData.name_en || payloadData.business_ar || t('Department Wise Patient Count')],
    [`${t('Period')}: ${moment(payloadData.start_date).format('DD/MM/YYYY')} - ${moment(payloadData.end_date).format('DD/MM/YYYY')}`],
    [],
    headers,
    ...formatted.map(row => headers.map(header => row[header]))
  ])
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } }
  ]
  worksheet['!cols'] = headers.map((header, index) => ({ wch: index === 0 ? 28 : 20 }))
  worksheet['!rows'] = [{ hpt: 26 }, { hpt: 18 }, { hpt: 8 }, { hpt: 24 }]
  const titleStyle = { font: { bold: true, sz: 14, color: { rgb: '1A365D' }, name: 'Calibri' }, alignment: { horizontal: 'left', vertical: 'center' } }
  const metaStyle = { font: { sz: 9, color: { rgb: '444444' }, name: 'Calibri' }, alignment: { horizontal: 'left', vertical: 'center' } }
  const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10, name: 'Calibri' }, fill: { patternType: 'solid', fgColor: { rgb: '1A365D' } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: { top: { style: 'thin', color: { rgb: '000000' } }, bottom: { style: 'thin', color: { rgb: '000000' } }, left: { style: 'thin', color: { rgb: '000000' } }, right: { style: 'thin', color: { rgb: '000000' } } } }
  const dataStyle = { font: { sz: 9, name: 'Calibri' }, alignment: { horizontal: 'center', vertical: 'center' }, border: { top: { style: 'hair', color: { rgb: 'CCCCCC' } }, bottom: { style: 'hair', color: { rgb: 'CCCCCC' } }, left: { style: 'hair', color: { rgb: 'CCCCCC' } }, right: { style: 'hair', color: { rgb: 'CCCCCC' } } } }
  worksheet.A1.s = titleStyle
  worksheet.A2.s = metaStyle
  headers.forEach((_, index) => {
    const cell = XLSX.utils.encode_cell({ r: 3, c: index })
    worksheet[cell].s = headerStyle
  })
  for (let row = 4; row < 4 + formatted.length; row++) {
    for (let col = 0; col < headers.length; col++) {
      const cell = XLSX.utils.encode_cell({ r: row, c: col })
      if (worksheet[cell]) worksheet[cell].s = dataStyle
    }
  }
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, t('Department Wise Patient Report'))
  XLSX.writeFile(workbook, 'department-wise-patient-report.xlsx')
}

const exportToPDF = (data, details, t, payloadData) => {
  const rows = details ? flattenDetails(data) : data
  const doc = new jsPDF('landscape')
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')
  const headers = details
    ? [t('Department'), t('Total Patients'), t('Unique Patients'), t('Patient Name'), t('File No'), t('Document ID'), t('Practitioner Name'), t('Visit ID'), t('Visit Date'), t('Visit')]
    : [t('Department'), t('Total Patients'), t('Unique Patients')]
  const body = (Array.isArray(rows) ? rows : []).map(row => {
    const base = [row?.Department || t('Unassigned'), row?.Count ?? 0, row?.PatientCount ?? 0]
    if (!details) return base
    return [...base, row?.PatientName || '', row?.FileNo || '', row?.DocumentID || '', row?.PractitionerName || '', row?.VisitID ?? '', row?.VisitDate || '', row?.Visit || row?.VisitCategory || '']
  })
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 5
  doc.setFillColor(26, 54, 93)
  doc.rect(0, 0, pageWidth, 24, 'F')
  if (payloadData.logo) {
    try { doc.addImage(payloadData.logo, 'PNG', margin, 2, 16, 16) } catch (_) {}
  }
  doc.setFont('Amiri', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(255, 255, 255)
  doc.text(payloadData.business_en || payloadData.name_en || payloadData.business_ar || t('Organization'), margin + 20, 9)
  doc.setFont('Amiri', 'normal')
  doc.setFontSize(11)
  doc.text(t('Department Wise Patient Count'), pageWidth - margin, 9, { align: 'right' })
  doc.setFontSize(7)
  if (payloadData.address) doc.text(payloadData.address, margin + 20, 15)
  doc.text(`${t('Printed On')}: ${moment().format('DD/MM/YYYY HH:mm')}`, pageWidth - margin, 15, { align: 'right' })
  doc.setFontSize(8)
  doc.text(`${t('Period')}: ${moment(payloadData.start_date).format('DD/MM/YYYY')} - ${moment(payloadData.end_date).format('DD/MM/YYYY')}`, margin, 27)
  doc.setDrawColor(200, 200, 200); doc.line(margin, 29.5, pageWidth - margin, 29.5)
  autoTable(doc, { head: [headers], body, startY: 33, theme: 'grid', styles: { font: 'Amiri', fontSize: 8, cellPadding: 3, lineColor: [204, 204, 204], textColor: [50, 50, 50] }, headStyles: { fillColor: [26, 54, 93], textColor: 255, halign: 'center', fontStyle: 'bold' } })
  doc.save('department-wise-patient-count.pdf')
}

const DepartmentWisePatientReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const businessInfo = useSelector(state => state.auth.buisnessInfo || {})
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading,
  } = useFetchData()
  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    'department_wise_patient_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      details: false,
    }
  )

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/patient/department-wise', {
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
      details: Boolean(payload.details),
    })
  }

  const handleChange = event => {
    const { name, value } = event.target
    setPayloadData(previous => ({
      ...previous,
      [name]: name === 'details' ? value === true || value === 'true' : value
    }))
  }

  const handleReset = () => {
    const resetPayload = {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      details: false,
    }
    setPayloadData(resetPayload)
  }

  const filterConfig = [
    { type: 'date', name: 'start_date', label: 'Start Date', width: 'w-[160px]' },
    { type: 'date', name: 'end_date', label: 'End Date', width: 'w-[160px]' },
    {
      type: 'dropdown',
      name: 'details',
      label: 'View',
      options: [
        { label: 'Summary', value: false },
        { label: 'Details', value: true }
      ],
      width: 'w-[150px]'
    }
  ]

  const groups = requestData?.data || []
  const showingDetails = groups.some(group => Array.isArray(group?.Details))
  const rows = showingDetails ? flattenDetails(groups) : groups

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('Department Wise Patient Count')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={payloadData}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={() => requestReport(payloadData)}
        onReset={handleReset}
        loading={loading}
        generateIcon={FaSearch}
        generateButtonType="primary"
        extraActions={(
          <>
            <CustomButton
              title={t('Export to Excel')}
              buttonType="success"
              icon={FaFileExcel}
              className="w-fit h-[38px]"
              onClick={() => exportToExcel(groups, showingDetails, t, { ...payloadData, ...businessInfo })}
            />
            <CustomButton
              title={t('Export to PDF')}
              buttonType="danger"
              icon={FaFilePdf}
              className="w-fit h-[38px]"
              onClick={() => exportToPDF(groups, showingDetails, t, { ...payloadData, ...businessInfo })}
            />
          </>
        )}
      />

      <Table
        data={rows}
        columns={showingDetails ? detailColumns : summaryColumns}
        loading={loading}
        showGridlines
      />
    </div>
  )
}

export default DepartmentWisePatientReport
